import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, MessageCircle, ArrowDown, Users } from "lucide-react";
import { SITE } from "../config/site";

const LINES = ["Matematica e fisica,", "spiegate come", "si deve."];

const SYMBOLS = [
    { t: "E = mc²", x: "7%", y: "16%", size: "text-2xl", depth: 140 },
    { t: "∫ f(x) dx", x: "80%", y: "20%", size: "text-3xl", depth: 220 },
    { t: "√(a² + b²)", x: "12%", y: "70%", size: "text-xl", depth: 90 },
    { t: "F = m · a", x: "74%", y: "64%", size: "text-2xl", depth: 60 },
    { t: "π r²", x: "88%", y: "82%", size: "text-xl", depth: 180 },
    { t: "Δv / Δt", x: "52%", y: "10%", size: "text-lg", depth: 260 },
];

const Hero = () => {
    const ref = useRef(null);
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 900], [0, 180]);
    const [count, setCount] = useState(null);

    useEffect(() => {
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/stats`).then((res) => setCount(res.data.users)).catch(() => {});
    }, []);

    return (
        <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-16">
            <motion.div style={{ y: bgY }} className="absolute inset-0 graph-paper" aria-hidden="true" />
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_0%,#020617_78%)]" aria-hidden="true" />

            {SYMBOLS.map((s, i) => (
                <ParallaxSymbol key={i} {...s} scrollY={scrollY} />
            ))}

            <div className="relative z-10 max-w-[1440px] mx-auto px-6 sm:px-10 w-full">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.4, duration: 1 }}
                    className="font-mono text-xs uppercase tracking-[0.35em] text-sky-400 mb-8"
                    data-testid="hero-eyebrow"
                >
                    Ripetizioni · Medie — Superiori
                </motion.p>

                <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-slate-50">
                    {LINES.map((line, i) => (
                        <span key={i} className="block overflow-hidden pb-1">
                            <motion.span
                                className="block"
                                initial={{ y: "115%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 1, delay: 0.25 + i * 0.18, ease: [0.22, 1, 0.36, 1] }}
                            >
                                {i === 2 ? <em className="text-sky-400">{line}</em> : line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.95, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-8 inline-flex items-center gap-3 border border-sky-400/50 bg-sky-400/10 px-5 py-3"
                    data-testid="hero-free-lesson-badge"
                >
                    <span className="w-2 h-2 bg-sky-400 animate-pulse" aria-hidden="true" />
                    <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.2em] text-sky-300">
                        Prima lezione gratis — se non fa per te, non paghi niente
                    </p>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300"
                    data-testid="hero-subtitle"
                >
                    Siamo in due, facciamo una cosa sola e la facciamo bene: ripetizioni di
                    matematica e fisica. Online ovunque, o a domicilio a Livorno e dintorni.
                    Ci chiami, ci mettiamo d'accordo, si comincia. Niente agenzie, niente intermediari.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.3, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-12 flex flex-wrap items-center gap-4"
                >
                    <a
                        data-testid="hero-call-button"
                        href={SITE.tutors[0].phoneHref}
                        className="group inline-flex items-center gap-3 bg-slate-50 text-[#020617] font-semibold px-8 py-4 hover:bg-sky-400 transition-colors duration-300"
                    >
                        <Phone size={18} className="transition-transform duration-300 group-hover:-rotate-12" />
                        Chiama {SITE.tutors[0].name}
                    </a>
                    <a
                        data-testid="hero-whatsapp-button"
                        href={SITE.tutors[1].whatsapp}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group inline-flex items-center gap-3 border border-slate-600 text-slate-100 font-semibold px-8 py-4 hover:border-sky-400 hover:text-sky-400 transition-colors duration-300"
                    >
                        <MessageCircle size={18} className="transition-transform duration-300 group-hover:scale-110" />
                        Scrivici su WhatsApp
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.8, duration: 1 }}
                    className="mt-16 flex flex-wrap items-center gap-6 font-mono text-xs text-slate-500"
                    data-testid="hero-phone-strip"
                >
                    {SITE.tutors.map((t) => (
                        <a key={t.id} href={t.phoneHref} className="hover:text-sky-400 transition-colors duration-300">
                            {t.name} · {t.phoneDisplay}
                        </a>
                    ))}
                    {count !== null && (
                        <span data-testid="hero-users-counter" className="inline-flex items-center gap-2 border border-slate-700 px-3 py-1.5 text-sky-300">
                            <Users size={12} /> {count} {count === 1 ? "iscritto" : "iscritti"}
                        </span>
                    )}
                </motion.div>
            </div>

            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.2, duration: 1 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-600"
                aria-hidden="true"
            >
                <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
                    <ArrowDown size={20} />
                </motion.div>
            </motion.div>
        </section>
    );
};

const ParallaxSymbol = ({ t, x, y, size, depth, scrollY }) => {
    const offset = useTransform(scrollY, [0, 900], [0, -depth]);
    return (
        <motion.span
            style={{ left: x, top: y, y: offset }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.25 }}
            transition={{ delay: 1.6, duration: 1.6 }}
            className={`absolute font-mono ${size} text-sky-400 select-none pointer-events-none hidden sm:block`}
            aria-hidden="true"
        >
            {t}
        </motion.span>
    );
};

export default Hero;
